import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight, MoreVertical, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, secondaryText, borderPrimary, whiteBG } from '../../../styling/stylingHelper';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { fetchInventorySetupReview, saveInventorySetupDecision, removeInventorySetupReviewProducts, SetupReviewError } from '../../../services/inventory/inventorySetupReviewService';
import type { SetupReviewProduct, SetupReviewPayload, SetupReviewDecision, SetupReviewMinimumDecision, SetupReviewBulkRemoval } from '../../../services/inventory/inventorySetupReviewTypes';
import { useFuzzySearch } from '../../../hooks/useFuzzySearch';

const field = cn('w-full min-h-[44px] rounded-lg border px-3 py-2 text-sm', borderPrimary, whiteBG, primaryText);
const button = cn('min-h-[44px] rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50', borderPrimary, primaryText, 'hover:bg-gray-50 dark:hover:bg-slate-800');
const panel = cn('rounded-xl border p-3 sm:p-4 space-y-2', borderPrimary);

export function InventorySetupReviewMenu({ onOpen, onExcelOpen }: { onOpen: () => void; onExcelOpen: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', escape); };
  }, [open]);
  return <div className="relative ml-auto flex-shrink-0" ref={ref}>
    <button type="button" className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors" onClick={() => setOpen(!open)} aria-label={t('inventory.setupReview.menu')} aria-expanded={open} aria-haspopup="menu"><MoreVertical className="w-5 h-5" /></button>
    {open && <div role="menu" className="absolute right-0 top-full z-50 mt-1 w-64 rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark py-1 shadow-lg">
      <button role="menuitem" type="button" className="w-full min-h-[44px] px-3 py-2 text-left text-sm leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark" onClick={() => { setOpen(false); onOpen(); }}>{t('inventory.setupReview.title')}</button>
      <button role="menuitem" type="button" className="w-full min-h-[44px] px-3 py-2 text-left text-sm leading-5 font-semibold text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark" onClick={() => { setOpen(false); onExcelOpen(); }}>{t('inventory.excelSetup.title')}</button>
    </div>}
  </div>;
}

interface Props { restaurantId: string; initialGroupId: string | null; onClose: () => void }

export default function InventorySetupReviewDrawer({ restaurantId, initialGroupId, onClose }: Props) {
  const { t } = useTranslation();
  const groups = useInventoryStore(s => s.groups);
  const [groupId, setGroupId] = useState(initialGroupId ?? (groups.length === 1 ? groups[0].id : ''));
  const [data, setData] = useState<SetupReviewPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkRemoval, setBulkRemoval] = useState<SetupReviewBulkRemoval | null>(null);
  const [bulkRemoved, setBulkRemoved] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Applied/dismissed products leave this run's queue only; reopening or rerunning starts fresh.
  const [handledKeys, setHandledKeys] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [limit, setLimit] = useState(40);
  // Capture one stable request tracker for this drawer's lifetime. Cleanup invalidates
  // whichever load is latest, including manual reloads started after the effect ran.
  const requestState = useRef({ generation: 0 }).current;
  const changedSections = useRef(new Set<string>()).current;
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const load = useCallback(async () => {
    if (!groupId) return;
    const request = ++requestState.generation;
    setLoading(true); setError(null);
    try {
      const payload = await fetchInventorySetupReview(restaurantId, groupId);
      if (request === requestState.generation) setData(payload);
    } catch (err) {
      console.error(err);
      if (request === requestState.generation) { setData(null); setError('loadFailed'); }
    } finally { if (request === requestState.generation) setLoading(false); }
  }, [restaurantId, groupId, requestState]);

  useEffect(() => {
    setData(null); setSelectedKey(null); setMobileDetail(false); setSearch('');
    setBulkRemoval(null); setBulkRemoved(null);
    setHandledKeys(new Set());
    void load();
    return () => { requestState.generation++; };
  }, [load, requestState]);

  // Refresh the underlying inventory page once on exit, not on every reviewed product.
  useEffect(() => () => {
    if (!changedSections.size) return;
    const store = useInventoryStore.getState();
    if (store.currentRestaurantId !== restaurantId) return;
    void Promise.all([
      ...Array.from(changedSections).filter(id => id in store.sectionProducts).map(id => store.loadSectionProducts(id)),
      store.invalidateCountReview(restaurantId),
    ]).catch(console.error);
  }, [restaurantId, changedSections]);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    const keyboard = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (!drawerRef.current?.getAttribute('aria-busy')) closeRef.current(); return; }
      if (e.key !== 'Tab') return;
      const focusable = drawerRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]');
      const visible = Array.from(focusable ?? []).filter(el => el.getClientRects().length > 0);
      const first = visible[0], last = visible[visible.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === drawerRef.current)) { e.preventDefault(); last?.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keyboard);
    return () => { document.removeEventListener('keydown', keyboard); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, []);

  const rows = useMemo(() => (data?.products ?? []).filter(p => !handledKeys.has(p.productKey) && p.reasons.includes('no_counted_home')), [data, handledKeys]);
  const filtered = useFuzzySearch({ data: rows, keys: ['productName', 'names', 'linkedProduct.supplier', 'productKey'], query: search, threshold: 0.35, enableSynonyms: false });
  const product = data?.products.find(p => p.productKey === selectedKey) ?? null;
  useEffect(() => { setLimit(40); }, [search, groupId]);
  useEffect(() => {
    if (!data) return;
    if (!filtered.some(p => p.productKey === selectedKey)) setSelectedKey(filtered[0]?.productKey ?? null);
  }, [data, filtered, selectedKey]);

  const finishProduct = (productKey: string) => {
    const nextKey = filtered.find((p, i) => i > filtered.findIndex(row => row.productKey === productKey) && p.productKey !== productKey)?.productKey
      ?? filtered.find(p => p.productKey !== productKey)?.productKey ?? null;
    setHandledKeys(current => new Set(current).add(productKey));
    setSelectedKey(nextKey); setError(null);
  };

  const runAgain = () => {
    setBulkRemoval(null); setBulkRemoved(null);
    setHandledKeys(new Set()); setData(null); setSelectedKey(null); setSearch('');
    setMobileDetail(false);
    void load();
  };

  const save = async (decision: SetupReviewDecision) => {
    setSaving(true); setError(null);
    try {
      const result = await saveInventorySetupDecision(restaurantId, decision);
      result.affectedSectionIds.forEach(id => changedSections.add(id));
      setData(current => {
        if (!current) return current;
        const affected = new Set(result.affectedProductKeys);
        const updated = new Map(result.products.map(p => [p.productKey, p]));
        const previousKeys = new Set(current.products.map(p => p.productKey));
        return { ...current,
          products: current.products.flatMap(p => affected.has(p.productKey) ? updated.has(p.productKey) ? [updated.get(p.productKey)!] : [] : [p])
            .concat(result.products.filter(p => !previousKeys.has(p.productKey))),
          linkTargets: current.linkTargets.filter(p => !affected.has(p.productKey)).concat(result.linkTargets),
        };
      });
      finishProduct(decision.productKey);
    } catch (err) {
      console.error(err);
      const reviewErrors: Record<string, string> = { review_changed: 'stale', review_product_not_found: 'stale' };
      setError(err instanceof SetupReviewError ? reviewErrors[err.code] ?? 'saveFailed' : 'saveFailed');
    } finally { setSaving(false); }
  };

  const removeQueue = async () => {
    if (!bulkRemoval || saving) return;
    setSaving(true); setError(null);
    try {
      const result = await removeInventorySetupReviewProducts(restaurantId, bulkRemoval);
      result.affectedSectionIds.forEach(id => changedSections.add(id));
      const removed = new Set(result.affectedProductKeys);
      setData(current => current && ({ ...current,
        products: current.products.filter(p => !removed.has(p.productKey)),
        linkTargets: current.linkTargets.filter(p => !removed.has(p.productKey)),
      }));
      setBulkRemoved(result.productsRemoved); setSelectedKey(null); setMobileDetail(false);
    } catch (err) {
      console.error(err);
      setError(err instanceof SetupReviewError && err.code === 'review_changed' ? 'stale' : 'saveFailed');
    } finally { setSaving(false); setBulkRemoval(null); }
  };

  const pendingCount = rows.length;
  return <>
    <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/60" onClick={() => { if (!saving) onClose(); }} />
    <div ref={drawerRef} role="dialog" aria-modal="true" aria-labelledby="setup-review-title" aria-busy={saving || undefined} tabIndex={-1} className={cn('fixed inset-y-0 right-0 z-50 w-full max-w-[1040px] flex flex-col shadow-2xl outline-none', whiteBG, primaryText)}>
      <header className={cn('flex items-start justify-between gap-3 p-4 sm:p-5 border-b', borderPrimary)}>
        <div className="min-w-0"><h2 id="setup-review-title" className="text-xl font-semibold">{t('inventory.setupReview.title')}</h2><p className={cn('text-sm mt-1', secondaryText)}>{t('inventory.setupReview.description')}</p></div>
        <button className={button} type="button" disabled={saving} onClick={onClose} aria-label={t('inventory.cancel')}><X size={20} /></button>
      </header>
      <div className={cn('px-4 sm:px-5 py-3 border-b flex flex-wrap items-center gap-3', borderPrimary)}>
        <label className="flex items-center gap-2 text-sm">{t('inventory.setupReview.inventory')}<select className={cn(field, 'w-auto max-w-[220px]')} value={groupId} disabled={saving || !!bulkRemoval} onChange={e => setGroupId(e.target.value)}><option value="">{t('inventory.setupReview.chooseInventory')}</option>{groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select></label>
        {data && <span className={cn('text-sm', secondaryText)}>{t('inventory.setupReview.progress', { pending: pendingCount })}</span>}
        <button type="button" className={button} disabled={!groupId || saving || loading || !!bulkRemoval} onClick={runAgain}>{t('inventory.setupReview.runAgain')}</button>
        <button type="button" className={cn(button, 'text-red-700 dark:text-red-200')} disabled={!pendingCount || saving || loading || !!bulkRemoval} onClick={() => {
          setBulkRemoved(null); setBulkRemoval({ groupId, products: rows.map(p => ({ productKey: p.productKey, fingerprint: p.fingerprint })) });
        }}>{t('inventory.setupReview.bulkRemove', { count: pendingCount })}</button>
      </div>
      {bulkRemoval && <section role="alertdialog" aria-labelledby="bulk-remove-title" aria-describedby="bulk-remove-description" className="mx-4 mt-3 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 space-y-3">
        <h3 id="bulk-remove-title" className="font-semibold text-red-800 dark:text-red-200">{t('inventory.setupReview.bulkRemoveTitle', { count: bulkRemoval.products.length })}</h3>
        <p id="bulk-remove-description" className={cn('text-sm', primaryText)}>{t('inventory.setupReview.bulkRemoveDescription', { count: bulkRemoval.products.length })}</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" autoFocus className={button} disabled={saving} onClick={() => setBulkRemoval(null)}>{t('inventory.cancel')}</button>
          <button type="button" className={cn(button, 'text-red-700 dark:text-red-200')} disabled={saving} onClick={() => { void removeQueue(); }}>{t('inventory.setupReview.bulkRemoveConfirm')}</button>
        </div>
      </section>}
      {bulkRemoved != null && <p role="status" className={cn('mx-4 mt-3 text-sm', primaryText)}>{t('inventory.setupReview.bulkRemoved', { count: bulkRemoved })}</p>}
      {error && <div role="alert" className="m-4 rounded-lg p-3 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 text-sm"><p>{t(`inventory.setupReview.${error}`)}</p><button className={cn(button, 'mt-2')} disabled={saving || loading || !!bulkRemoval} onClick={() => { void load(); }}>{t('inventory.setupReview.reload')}</button></div>}
      {!groupId ? <p className={cn('p-5', secondaryText)}>{t('inventory.setupReview.chooseInventory')}</p> : loading && !data ? <p className={cn('p-5', secondaryText)}>{t('inventory.loading')}</p> : data && <div className="flex-1 min-h-0 flex flex-col sm:flex-row">
        <aside className={cn('flex-col min-h-0 w-full sm:w-[300px] sm:shrink-0 sm:border-r', borderPrimary, mobileDetail ? 'hidden sm:flex' : 'flex')}>
          <div className="p-3"><input className={field} aria-label={t('inventory.setupReview.search')} placeholder={t('inventory.setupReview.search')} value={search} disabled={saving || !!bulkRemoval} onChange={e => setSearch(e.target.value)} /></div>
          <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
            {filtered.slice(0, limit).map(p => <button key={p.productKey} disabled={saving || !!bulkRemoval} className={cn('w-full text-left rounded-lg p-3 border disabled:opacity-50', borderPrimary, selectedKey === p.productKey ? 'bg-emerald-50 dark:bg-emerald-950/40' : 'hover:bg-gray-50 dark:hover:bg-slate-800')} onClick={() => { setSelectedKey(p.productKey); setMobileDetail(true); }}>
              <span className="flex items-start gap-2"><span className="flex-1 text-sm font-medium break-words">{p.productName || t('inventory.setupReview.unnamed')}</span><ChevronRight size={16} /></span>
              <span className={cn('block mt-1 text-xs', secondaryText)}>{t(`inventory.setupReview.source.${p.source}`)}{p.minimum ? ` · ${p.minimum.count} ${p.minimum.countUnit}` : ''}</span>
            </button>)}
            {filtered.length === 0 && <p className={cn('p-3 text-sm', secondaryText)}>{t('inventory.setupReview.empty')}</p>}
            {filtered.length > limit && <button className={cn(button, 'w-full')} onClick={() => setLimit(value => value + 40)}>{t('inventory.setupReview.showMore')}</button>}
          </div>
        </aside>
        <main className={cn('flex-1 min-w-0 min-h-0 overflow-y-auto p-4 sm:p-5', mobileDetail ? 'block' : 'hidden sm:block')}>
          <button className={cn(button, 'sm:hidden mb-3 flex items-center gap-2')} onClick={() => setMobileDetail(false)} disabled={saving}><ArrowLeft size={16} />{t('inventory.setupReview.backToList')}</button>
          {product ? <ReviewEditor key={`${product.productKey}:${product.fingerprint}`} product={product} data={data} saving={saving || !!bulkRemoval} onSave={save} onDismiss={() => finishProduct(product.productKey)} /> : <p className={secondaryText}>{t('inventory.setupReview.empty')}</p>}
        </main>
      </div>}
    </div>
  </>;
}

function ReviewEditor({ product, data, saving, onSave, onDismiss }: { product: SetupReviewProduct; data: SetupReviewPayload; saving: boolean; onSave: (decision: SetupReviewDecision) => Promise<void>; onDismiss: () => void }) {
  const { t } = useTranslation();
  const currentSections = data.sections.filter(s => s.groupId === data.groupId && product.assignments.some(a => a.sectionId === s.id));
  const [locations, setLocations] = useState(currentSections.map(s => s.id));
  const initialMinimum = product.minimum ? String(product.minimum.count) : '';
  const [minimumCount, setMinimumCount] = useState(initialMinimum);
  const [validation, setValidation] = useState<string | null>(null);
  const minimumAssignment = product.minimum
    ? product.assignments.find(a => a.countUnit === product.minimum!.countUnit
      && Math.abs(a.countUnitToStdFactor * product.minimum!.count - product.minimum!.countInStdUnit) < 0.000001)
    : product.assignments.find(a => a.productKey === product.productKey) ?? product.assignments[0];
  const minimumUnit = product.minimum?.countUnit ?? minimumAssignment?.countUnit ?? '';
  const inventoryName = data.groups.find(g => g.id === data.groupId)?.name ?? '';
  const possibleDuplicates = data.linkTargets.filter(target => target.source === 'order' && target.resolved
    && product.possibleDuplicateKeys.includes(target.productKey));

  const submit = () => {
    setValidation(null);
    let minimum: SetupReviewMinimumDecision = { mode: 'keep' };
    if (minimumCount !== initialMinimum) {
      const count = Number(minimumCount);
      if (!Number.isFinite(count) || count < 0) { setValidation('invalidMinimum'); return; }
      if (minimumCount.trim() === '' || count === 0) minimum = { mode: 'clear' };
      else if (!minimumAssignment) { setValidation('minimumUnitUnavailable'); return; }
      else minimum = { mode: 'set', count, assignmentId: minimumAssignment.id };
    }
    void onSave({ groupId: data.groupId, productKey: product.productKey, fingerprint: product.fingerprint,
      action: locations.length ? 'keep' : 'remove',
      ...(locations.length ? { keptSectionIds: locations } : {}), minimum });
  };

  return <fieldset disabled={saving} className="space-y-5">
    <legend className="sr-only">{product.productName || t('inventory.setupReview.unnamed')}</legend>
    <div>
      <h3 className="text-lg font-semibold break-words">{product.productName || t('inventory.setupReview.unnamed')}</h3>
      <p className={cn('mt-1 text-sm', secondaryText)}>{t(`inventory.setupReview.source.${product.source}`)}{product.linkedProduct.supplier ? ` · ${product.linkedProduct.supplier}` : ''}</p>
      {product.source !== 'custom' && <p className={cn('mt-1 text-sm break-words', secondaryText)}>{product.linkedProduct.resolved
        ? t('inventory.setupReview.linkedProduct', { name: product.linkedProduct.productName || product.productName })
        : t('inventory.setupReview.unresolved')}</p>}
    </div>
    {possibleDuplicates.length > 0 && <div className="rounded-lg p-3 text-sm bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200">
      <p className="font-semibold">{t('inventory.setupReview.possibleDuplicate')}</p>
      {possibleDuplicates.map(target => <p key={target.productKey} className="mt-1 break-words">
        {data.products.find(p => p.productKey === target.productKey)?.productName || target.productName || t('inventory.setupReview.unnamed')}
        {target.supplier ? ` · ${target.supplier}` : ''}
      </p>)}
    </div>}
    <section className={panel}>
      <h4 className="font-semibold text-sm">{t('inventory.setupReview.currentSections')}</h4>
      <p className={cn('text-sm', secondaryText)}>{t('inventory.setupReview.sectionsHint')}</p>
      {currentSections.map(section => <label key={section.id} className="flex items-center gap-3 min-h-[44px] py-2 text-sm">
        <input type="checkbox" checked={locations.includes(section.id)} onChange={e => setLocations(current => e.target.checked ? [...current, section.id] : current.filter(id => id !== section.id))} />
        <span>{section.name}</span>
      </label>)}
      <button type="button" className={cn(button, 'w-full')} disabled={!locations.length} onClick={() => setLocations([])}>{t('inventory.setupReview.removeAllSections')}</button>
      {!locations.length && <p className={cn('text-sm', secondaryText)}>{t('inventory.setupReview.removeAllHint', { inventory: inventoryName })}</p>}
    </section>
    <section className={panel}>
      <label className="block text-sm font-semibold" htmlFor="setup-review-minimum">{t('inventory.setupReview.minimum')}</label>
      <div className="flex items-center gap-3">
        <input id="setup-review-minimum" className={cn(field, 'max-w-[180px]')} type="number" min="0" step="any" value={minimumCount} placeholder={t('inventory.setupReview.noMinimum')} onChange={e => setMinimumCount(e.target.value)} />
        <span className={cn('text-sm', secondaryText)}>{minimumUnit}</span>
      </div>
      <p className={cn('text-xs', secondaryText)}>{t('inventory.setupReview.minimumHint')}</p>
    </section>
    {validation && <p role="alert" className="text-sm text-red-700 dark:text-red-200">{t(`inventory.setupReview.${validation}`)}</p>}
    <section className={panel}>
      <p className={cn('text-sm', secondaryText)}>{t('inventory.setupReview.removeEverywhereHint', { count: new Set(product.assignments.map(a => a.sectionId)).size })}</p>
      <button type="button" className="min-h-[44px] w-full rounded-lg px-3 py-2 text-sm font-semibold text-red-700 dark:text-red-200 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50" onClick={() => { void onSave({ groupId: data.groupId, productKey: product.productKey, fingerprint: product.fingerprint, action: 'removeAll' }); }}>{t('inventory.setupReview.removeEverywhere')}</button>
    </section>
    <div className="flex flex-col sm:flex-row gap-2">
      <button type="button" className={button} onClick={onDismiss}>{t('inventory.setupReview.dismiss')}</button>
      <button type="button" className="min-h-[44px] flex-1 rounded-lg px-4 py-2 text-sm font-semibold bg-emerald-700 dark:bg-emerald-700 text-white dark:text-white hover:bg-emerald-800 dark:hover:bg-emerald-800 disabled:opacity-50" onClick={submit}>{saving ? t('inventory.loading') : t('inventory.setupReview.applyNext')}</button>
    </div>
  </fieldset>;
}
